import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Zap, Train, LandPlot, MapPin } from "lucide-react";
import { categories } from "../data/services";

const THERMAL_IMG =
  "https://customer-assets.emergentagent.com/job_drone-survey-pro-4/artifacts/gw30j23r_b0f383fe-d9cb-4b61-98cc-08c612d05d19.webp";
const SURVEY_IMG =
  "https://customer-assets.emergentagent.com/job_drone-survey-pro-4/artifacts/grhqax9j_Screenshot%202026-07-08%20150135.webp";

export const Services = () => {
  const [active, setActive] = useState(categories[0].id);
  const current = categories.find((c) => c.id === active);

  return (
    <section id="services" className="py-24 bg-[#f8f9fa] border-y border-gray-200">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#8b8f99]">
            What we do
          </span>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-black text-[#10193f]">
            15 specialised drone service lines
          </h2>
          <p className="mt-4 text-[#52525b]">
            End-to-end aerial capability grouped across three core disciplines —
            from survey-grade mapping to cinematic media.
          </p>
        </div>

        {/* Category tabs */}
        <div className="mt-10 flex flex-wrap gap-2" data-testid="service-tabs">
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`service-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`rounded-sm px-5 py-3 text-sm font-semibold transition-all border ${
                active === c.id
                  ? "bg-[#10193f] text-white border-[#10193f]"
                  : "bg-white text-[#10193f] border-gray-200 hover:border-[#10193f]/40"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <p className="mt-4 text-sm font-semibold uppercase tracking-wider text-[#8b8f99]">
          {current.tagline}
        </p>

        {/* Flagship survey project */}
        {active === "survey" && (
          <motion.div
            data-testid="survey-flagship-project"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-6 grid md:grid-cols-2 gap-0 rounded-sm overflow-hidden border border-gray-200 bg-white"
          >
            <div className="p-7 sm:p-9 order-2 md:order-1">
              <div className="flex items-center gap-2 text-[#8b8f99]">
                <MapPin className="h-5 w-5" strokeWidth={1.75} />
                <span className="text-xs font-bold uppercase tracking-widest">
                  CIDCO · NAINA · Panvel
                </span>
              </div>
              <h3 className="mt-3 font-heading text-xl sm:text-2xl font-bold text-[#10193f]">
                CIDCO – NAINA Land Survey — 3,000 Acres
              </h3>
              <p className="mt-3 text-sm text-[#52525b] leading-relaxed">
                With <span className="font-semibold text-[#10193f]">100+ land
                survey projects</span> delivered, our flagship engagement is the
                CIDCO NAINA land survey spanning 3,000 acres in Panvel —
                high-accuracy drone photogrammetry producing orthomosaics,
                contours and GIS-ready deliverables at scale.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {[
                  "3,000-acre coverage",
                  "High-resolution orthomosaics",
                  "Contour & DEM/DSM generation",
                  "GIS-compatible deliverables",
                ].map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-sm text-[#10193f]/80"
                  >
                    <LandPlot className="mt-0.5 h-4 w-4 shrink-0 text-[#8b8f99]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative h-56 md:h-auto order-1 md:order-2 bg-[#f2f4f8]">
              <img
                src={SURVEY_IMG}
                alt="CIDCO NAINA drone land survey orthomosaic"
                className="w-full h-full object-cover"
              />
              <span className="absolute top-3 left-3 rounded-sm bg-[#10193f] text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1">
                100+ Projects · Flagship
              </span>
            </div>
          </motion.div>
        )}

        {/* Flagship inspection project */}
        {active === "inspection" && (
          <motion.div
            data-testid="inspection-flagship-project"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-6 grid md:grid-cols-2 gap-0 rounded-sm overflow-hidden border border-gray-200 bg-white"
          >
            <div className="relative h-56 md:h-auto">
              <img
                src={THERMAL_IMG}
                alt="Thermal powerline inspection, Central Railway"
                className="w-full h-full object-cover"
              />
              <span className="absolute top-3 left-3 rounded-sm bg-[#10193f] text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1">
                Flagship Project
              </span>
            </div>
            <div className="p-7 sm:p-9">
              <div className="flex items-center gap-2 text-[#8b8f99]">
                <Train className="h-5 w-5" strokeWidth={1.75} />
                <span className="text-xs font-bold uppercase tracking-widest">
                  Central Railway · Ghat Section
                </span>
              </div>
              <h3 className="mt-3 font-heading text-xl sm:text-2xl font-bold text-[#10193f]">
                Powerline Inspection — Lonavala & Igatpuri Ghats
              </h3>
              <p className="mt-3 text-sm text-[#52525b] leading-relaxed">
                Thermal drone inspection of railway overhead traction and
                transmission infrastructure across the challenging Lonavala and
                Igatpuri ghat sections for Central Railway — detecting hotspots
                and defects for safer, preventive maintenance.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {[
                  "Thermal camera hotspot detection",
                  "Overhead traction line inspection",
                  "Difficult ghat-terrain coverage",
                  "Preventive maintenance reporting",
                ].map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-sm text-[#10193f]/80"
                  >
                    <Zap className="mt-0.5 h-4 w-4 shrink-0 text-[#8b8f99]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}

        {active === "inspection" && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="inspection-project-gallery">
            {[
              {
                url: "https://customer-assets.emergentagent.com/job_drone-survey-pro-4/artifacts/gw30j23r_b0f383fe-d9cb-4b61-98cc-08c612d05d19.webp",
                label: "Thermal Hotspot Capture — 41.5°C",
                fit: "object-cover",
              },
              {
                url: "https://customer-assets.emergentagent.com/job_drone-survey-pro-4/artifacts/n8ccuxqp_WhatsApp%20Image%202026-07-08%20at%203.07.05%20PM.jpeg",
                label: "Drone Thermography Report — 110 KV Lines",
                fit: "object-contain",
              },
            ].map((g, i) => (
              <figure
                key={i}
                data-testid={`inspection-gallery-${i}`}
                className="group relative h-64 overflow-hidden rounded-sm border border-gray-200 bg-[#f2f4f8]"
              >
                <img
                  src={g.url}
                  alt={g.label}
                  loading="lazy"
                  className={`w-full h-full ${g.fit} transition-transform duration-500 group-hover:scale-105`}
                />
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#10193f]/85 to-transparent" />
                <figcaption className="absolute bottom-3 left-3 right-3 text-white font-heading font-bold text-sm">
                  {g.label}
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        {/* Service grid */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200 rounded-sm overflow-hidden">
          {current.services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group bg-white p-7 hover:bg-[#10193f] transition-colors duration-300"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#10193f]/5 text-[#10193f] group-hover:bg-white/10 group-hover:text-white transition-colors">
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-heading text-xl font-bold text-[#10193f] group-hover:text-white transition-colors">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-[#52525b] group-hover:text-white/70 transition-colors">
                  {s.summary}
                </p>
                <ul className="mt-5 space-y-2">
                  {s.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-start gap-2 text-sm text-[#10193f]/80 group-hover:text-white/80 transition-colors"
                    >
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8b8f99] group-hover:text-white" />
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
